import apiClient from "./client";
import { ENDPOINTS } from "./endpoints";
import { getUserIds } from "./session";

export const CONFIG_KEYS = [
  "lead_source",
  "property_type",
  "loan_type",
  "employment_type",
  "tenure",
  "gender_list",
  "govt_id_type",
  "nationality",
  "civil_status",
  "permanent_residence_type",
  "source_of_income",
  "relationship_with_borrower",
  "financier_list",
];

// financier_list returns raw fields (id, logo, financierlang, ...), not the
// generic {id, label, value} dropdown shape — keep it out of normalizeOption.
const RAW_CONFIG_KEYS = ["financier_list"];

export const fetchConfig = () =>
  apiClient.post(ENDPOINTS.auth.config, {
    source: "SFA",
    ...getUserIds(),
    config_data: CONFIG_KEYS,
  });

const normalizeOption = (item, idx) => {
  if (item == null) return null;
  if (typeof item === "string") return { id: idx, label: item, value: item };
  const value = item.value ?? item.id ?? item.code ?? item.key ?? "";
  const label = item.label ?? item.name ?? item.title ?? item.text ?? String(value);
  return { id: item.id ?? value ?? idx, label, value: String(value) };
};

export const normalizeConfigResponse = (raw) => {
  const map = raw?.data?.config_data || raw?.data || raw || {};
  const result = {};
  Object.keys(map).forEach((key) => {
    const list = map[key];
    if (!Array.isArray(list)) {
      result[key] = [];
      return;
    }
    result[key] = RAW_CONFIG_KEYS.includes(key) ? list : list.map(normalizeOption).filter(Boolean);
  });
  return result;
};

export const financierDisplayName = (financier) => {
  const langEntry = Array.isArray(financier?.financierlang) ? financier.financierlang[0] : financier?.financierlang;
  return langEntry?.financier_name || "Financier " + financier?.id;
};

// Dependent dropdown: options change with the selected Lead Source Type, so this
// is fetched separately from the one-shot CONFIG_KEYS batch above.
export const fetchPartnerList = (sourceType) =>
  apiClient.post(ENDPOINTS.applicationForm.partnerList, {
    source: "SFA",
    ...getUserIds(),
    source_type: Number(sourceType),
  });

// document_list needs lead_id (and optional financier_id), so it's fetched on its
// own once a lead exists rather than being part of the generic CONFIG_KEYS batch.
export const fetchDocumentList = (leadId, financierId) =>
  apiClient.post(ENDPOINTS.auth.config, {
    source: "SFA",
    ...getUserIds(),
    config_data: ["document_list"],
    lead_id: leadId,
    financier_id: financierId,
  });

// Reshapes the backend's document tree (root nodes = categories, each with a
// `child` array of required docs) into the {key, name, reqs, docIds} shape the
// doc-upload flow keys everything by (reqId scheme, CAP-per-requirement,
// tagging sheets).
export const buildDocumentCategories = (tree) => {
  if (!Array.isArray(tree)) return [];
  return tree
    .filter((node) => Array.isArray(node?.child) && node.child.length > 0)
    .map((node) => ({
      key: String(node.id),
      name: node.doclang?.name || "Documents",
      reqs: node.child.map((c) => c.doclang?.name || "Document"),
      docIds: node.child.map((c) => c.id),
      // Mandatory for this app (is_required_app, falling back to is_required).
      // Doc types hidden from the app (show_in_app 0, e.g. co-borrower IDs)
      // are never enforced here.
      required: node.child.map((c) => c.show_in_app !== 0 && !!(c.is_required_app ?? c.is_required)),
      minUpload: node.child.map((c) => Math.max(1, Number(c.min_upload) || 1)),
    }));
};
